import { Component, inject, Injector, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { environment } from '../environments/environment';
import { distinctUntilChanged, Subscription } from 'rxjs';
import {
  LogoConfig,
  NavigationItem,
  NavigationService,
  ToastComponent,
} from '@angular-monorepo/ui';
import { TranslateService } from '@ngx-translate/core';
import { AuthConfigService, AuthService } from '@angular-monorepo/auth';
import { ConfigService } from '../environments/environment.service';

@Component({
  imports: [RouterModule, ToastComponent],
  selector: 'app-root',
  template: `
    <router-outlet></router-outlet>
    <lib-toast></lib-toast>
  `,
  styles: [],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'ditrack';
  environment = environment;
  private langChangeSub!: Subscription;
  private roleSub!: Subscription;
  private dataInitialized = false;

  private readonly navigationService = inject(NavigationService);
  private readonly configService = inject(ConfigService);
  private readonly translate = inject(TranslateService);
  private readonly authConfigService = inject(AuthConfigService);
  private readonly authService = inject(AuthService);
  private readonly injector = inject(Injector);

  constructor() {
    const savedLang = localStorage.getItem('lang') || 'es';
    this.translate.setDefaultLang('es');
    this.translate.use(savedLang);

    this.setupAuthConfig();
  }

  ngOnInit() {
    this.setupNavigation();
    this.setupUserNavigation();
    this.updateLogo();
    this.setupBackground();
    this.setupFooter();
    this.navigationService.updateTopBarBgColor('#414769');
    this.navigationService.updateAccentColor('#b0e7e7');
    this.navigationService.updateAvatarConfig({
      type: 'initials',
      backgroundColor: '#b0e7e7',
      color: '#414769',
    });

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.setupNavigation();
      this.setupUserNavigation();
    });

    this.roleSub = this.authService.userRoles$.pipe(
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
    ).subscribe((roles) => {
      this.setupNavigation();
      this.setupUserNavigation();
      if (roles.length > 0 && !this.dataInitialized) {
        this.dataInitialized = true;
        this.initializeServices();
      }
    });
  }

  ngOnDestroy() {
    this.langChangeSub?.unsubscribe();
    this.roleSub?.unsubscribe();
  }

  private async initializeServices(): Promise<void> {
    const { TimeTrackingService, ClientService } = await import('@angular-monorepo/ditrack');
    const timeTrackingService = this.injector.get(TimeTrackingService);
    timeTrackingService.configure({
      apiUrl: this.configService.getApiUrl(),
    });
    if (this.authService.hasRole('admin')) {
      const clientService = this.injector.get(ClientService);
      clientService.loadClients();
    }
  }

  private setupAuthConfig() {
    this.authConfigService.setConfig({
      apiUrl: this.configService.getApiUrl(),
      postLoginRoute: '/tiempo/my-time',
      enableOwnRolesRequest: false,
    });
  }

  private setupNavigation(): void {
    const keys = [
      'sidebar.tiempo',
      'sidebar.hojaDeHoras',
      'sidebar.miTiempo',
      'sidebar.dashboard',
      'sidebar.calendario',
      'sidebar.informes',
      'sidebar.resumenEquipo',
      'sidebar.resumenActividad',
      'sidebar.resumenProyectos',
      'sidebar.resumenTareas',
      'sidebar.informeDetallado',
      'sidebar.gestionar',
      'sidebar.proyectos',
      'sidebar.clientes',
      'sidebar.etiquetas',
      'sidebar.fases',
      'sidebar.categorias',
      'sidebar.workspace',
      'sidebar.miembros',
      'sidebar.equipos',
      'sidebar.projectManagers',
      'sidebar.fxRates',
      'sidebar.reminders',
      'sidebar.notifications',
    ];

    this.translate.get(keys).subscribe((t) => {
      const navigationItems: NavigationItem[] = [
        {
          name: t['sidebar.tiempo'],
          href: '/tiempo',
          icon: 'ti ti-clock-hour-4',
          current: false,
          children: [
            {
              name: t['sidebar.dashboard'],
              href: '/tiempo/dashboard',
              icon: 'ti ti-layout-dashboard',
              current: false,
            },
            {
              name: t['sidebar.miTiempo'],
              href: '/tiempo/my-time',
              icon: 'ti ti-player-play',
              current: false,
            },
            {
              name: t['sidebar.hojaDeHoras'],
              href: '/tiempo/timesheet',
              icon: 'ti ti-table',
              current: false,
            },
            {
              name: t['sidebar.calendario'],
              href: '/tiempo/calendar',
              icon: 'ti ti-calendar',
              current: false,
            },
          ],
        },
      ];

      if (this.authService.hasRole('admin')) {
        navigationItems.push(
          {
            name: t['sidebar.informes'],
            href: '/informes',
            icon: 'ti ti-report-analytics',
            current: false,
            children: [
              {
                name: t['sidebar.resumenEquipo'],
                href: '/informes/team-summary',
                icon: 'ti ti-users',
                current: false,
              },
              {
                name: t['sidebar.resumenActividad'],
                href: '/informes/activity',
                icon: 'ti ti-chart-bar',
                current: false,
              },
              {
                name: t['sidebar.resumenTareas'],
                href: '/informes/task-summary',
                icon: 'ti ti-subtask',
                current: false,
              },
              {
                name: t['sidebar.informeDetallado'],
                href: '/informes/detailed',
                icon: 'ti ti-list-details',
                current: false,
              },
            ],
          },
          {
            name: t['sidebar.gestionar'],
            href: '/gestionar',
            icon: 'ti ti-settings',
            current: false,
            children: [
              {
                name: t['sidebar.proyectos'],
                href: '/gestionar/projects',
                icon: 'ti ti-folder',
                current: false,
              },
              {
                name: t['sidebar.clientes'],
                href: '/gestionar/clients',
                icon: 'ti ti-users-group',
                current: false,
              },
              {
                name: t['sidebar.etiquetas'],
                href: '/gestionar/tags',
                icon: 'ti ti-tags',
                current: false,
              },
              {
                name: t['sidebar.fases'],
                href: '/gestionar/phases',
                icon: 'ti ti-stack-2',
                current: false,
              },
              {
                name: t['sidebar.categorias'],
                href: '/gestionar/categories',
                icon: 'ti ti-category-2',
                current: false,
              },
            ],
          },
          {
            name: t['sidebar.workspace'],
            href: '/workspace',
            icon: 'ti ti-building-skyscraper',
            current: false,
            children: [
              {
                name: t['sidebar.miembros'],
                href: '/workspace/members',
                icon: 'ti ti-user',
                current: false,
              },
              {
                name: t['sidebar.equipos'],
                href: '/workspace/teams',
                icon: 'ti ti-hierarchy-2',
                current: false,
              },
              {
                name: t['sidebar.projectManagers'],
                href: '/workspace/project-managers',
                icon: 'ti ti-user-star',
                current: false,
              },
              {
                name: t['sidebar.fxRates'],
                href: '/workspace/fx-rates',
                icon: 'ti ti-currency-dollar',
                current: false,
              },
              {
                name: t['sidebar.reminders'],
                href: '/workspace/reminders',
                icon: 'ti ti-bell-ringing',
                current: false,
              },
              {
                name: t['sidebar.notifications'],
                href: '/notifications',
                icon: 'ti ti-bell',
                current: false,
              },
            ],
          }
        );
      }

      this.navigationService.updateNavigation(navigationItems);
    });
  }

  private updateLogo() {
    const logoConfig: LogoConfig = {
      type: 'svg',
      svgContent: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 288 86" width="260" height="78" preserveAspectRatio="xMidYMid meet" style="display:block;">
        <text x="24" y="36" font-family="system-ui, -apple-system, sans-serif" font-size="30" font-weight="700" fill="white" letter-spacing="1">Ditech</text>
        <text x="24" y="62" font-family="system-ui, -apple-system, sans-serif" font-size="18" font-weight="400" fill="white" opacity="0.85">Group</text>
        <rect x="0" y="80" width="288" height="6" fill="#b0e7e7"/>
      </svg>`,
      alt: 'Ditech Time Tracker',
      class: 'mx-auto',
    };

    this.navigationService.updateLogo(logoConfig);
  }

  private setupBackground() {
    this.navigationService.updateBackground('assets/images/invitation-bg.png');
  }

  private setupFooter() {
    this.navigationService.updateFooterConfig({
      showDefault: false,
      hideForgotPasswordLink: false,
      hideRegisterLink: false,
      hideGoogleLoginButton: false,
      hideMicrosoftLoginButton: false,
      customElements: [
        {
          type: 'component',
          content: 'lib-theme-selector',
          props: { variant: 'toggle' },
        },
        {
          type: 'component',
          content: 'lib-i18n',
          props: {
            class: 'block',
            variant: 'toggle',
          },
        },
      ],
    });
  }

  private setupUserNavigation(): void {
    const logoutLabel = this.translate.instant('common.logout');

    this.navigationService.updateUserNavigation([
      {
        label: logoutLabel,
        icon: 'ti ti-logout',
        command: () => this.signOut(),
      },
    ]);
  }

  private signOut(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('refresh_token');

    globalThis.location.href = '/login';
  }
}
